import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";

import { supabase } from "@/integrations/supabase/client";

const SearchSchema = z.object({
  next: z.string().optional(),
});

export const Route = createFileRoute("/reset-password")({
  ssr: false,
  validateSearch: SearchSchema,
  component: ResetPasswordPage,
  head: () => ({
    meta: [
      { title: "Set your password · the Vibing Company®" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function safeNext(raw: string | undefined): string {
  if (!raw) return "/dashboard";
  if (!raw.startsWith("/") || raw.startsWith("//")) return "/dashboard";
  return raw;
}

function ResetPasswordPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/reset-password" });
  const [ready, setReady] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [state, setState] = useState<"idle" | "saving" | "done" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    // Recovery links arrive with auth params in the URL hash. Supabase
    // detects them automatically and emits PASSWORD_RECOVERY. We just need
    // to wait for a session before allowing the form to submit.
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    if (hash.includes("error=")) {
      const params = new URLSearchParams(hash.replace(/^#/, ""));
      const desc =
        params.get("error_description") ||
        params.get("error") ||
        "This link is invalid or has expired.";
      setLinkError(desc.replace(/\+/g, " "));
      return;
    }

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || (session && !ready)) {
        setReady(true);
      }
    });

    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
    });

    const timeout = window.setTimeout(() => {
      if (!ready) {
        setLinkError("We couldn't verify your link. Request a new one and try again.");
      }
    }, 8000);

    return () => {
      sub.subscription.unsubscribe();
      window.clearTimeout(timeout);
    };
  }, [ready]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    if (password.length < 8) {
      setState("error");
      setMessage("Use at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setState("error");
      setMessage("Passwords don't match.");
      return;
    }
    setState("saving");
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setState("error");
      setMessage(error.message);
      return;
    }
    setState("done");
    const dest = safeNext(search.next);
    window.setTimeout(() => navigate({ to: dest, replace: true }), 800);
  };

  return (
    <div className="min-h-dvh bg-background flex items-center justify-center px-6">
      <main id="main" tabIndex={-1} className="w-full max-w-md">
        <Link
          to="/"
          className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground hover:text-foreground"
        >
          ← the Vibing Company®
        </Link>
        <h1 className="mt-8 font-display text-5xl tracking-tight text-foreground leading-[1.05]">
          Set your password
        </h1>
        <p className="mt-4 text-muted-foreground text-base leading-relaxed">
          Choose a password to sign in to your project dashboard.
        </p>

        {linkError ? (
          <div role="alert" className="mt-10 rounded-2xl border border-destructive/20 bg-destructive/5 p-6">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-destructive">
              Link problem
            </p>
            <p className="mt-3 text-foreground">{linkError}</p>
            <Link
              to="/dashboard"
              className="mt-6 inline-block text-sm underline underline-offset-4 text-muted-foreground hover:text-foreground"
            >
              Request a new link →
            </Link>
          </div>
        ) : state === "done" ? (
          <div role="status" className="mt-10 rounded-2xl border border-foreground/10 bg-white p-6">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
              All set
            </p>
            <p className="mt-3 text-foreground">Password saved. Taking you to your dashboard…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-4">
            <div>
              <label htmlFor="new-password" className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                New password
              </label>
              <input
                id="new-password"
                name="new-password"
                type="password"
                autoComplete="new-password"
                minLength={8}
                aria-describedby="new-password-hint"
                required
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                aria-invalid={state === "error" || undefined}
                className="mt-2 w-full rounded-xl border border-foreground/15 bg-white px-4 py-3.5 text-foreground placeholder:text-foreground/30 focus:outline-none focus:ring-2 focus:ring-foreground/30"
              />
            </div>
            <p id="new-password-hint" className="text-xs text-muted-foreground">
              Use at least 8 characters.
            </p>
            <div>
              <label htmlFor="confirm-password" className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                Confirm password
              </label>
              <input
                id="confirm-password"
                name="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-2 w-full rounded-xl border border-foreground/15 bg-white px-4 py-3.5 text-foreground placeholder:text-foreground/30 focus:outline-none focus:ring-2 focus:ring-foreground/30"
              />
            </div>
            <button
              type="submit"
              disabled={state === "saving" || !ready}
              className="w-full rounded-xl bg-foreground text-background px-4 py-3.5 text-sm font-medium tracking-wide [transition-property:opacity,transform] duration-200 hover:opacity-90 active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100"
            >
              {state === "saving" ? "Saving…" : ready ? "Save password" : "Verifying link…"}
            </button>
            <div aria-live="assertive">
              {state === "error" && message && (
                <p className="text-sm text-destructive">Error: {message}</p>
              )}
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
